import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";

// Ógreiddir Reikningar (Unpaid Invoices) Details Component
export const OgreidirReikningarDetails = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ vextir: 17.7, greidslus: 250, drattarvextir: 15.25 });

  useEffect(() => {
    fetchUnpaidInvoices();
  }, []);

  const fetchUnpaidInvoices = async () => {
    try {
      // Fetch all loans
      const loansResponse = await axios.get(`${API}/loans`);
      const loans = loansResponse.data;
      
      // Fetch settings
      const settingsResponse = await axios.get(`${API}/settings`);
      setSettings(settingsResponse.data);
      
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      
      const unpaidList = [];
      
      // Check each loan for unpaid invoices in current month
      loans.forEach(loan => {
        if (loan.status === 'disbursed' && loan.payment_schedule) {
          loan.payment_schedule.forEach((payment, index) => {
            if (!payment.date) return;
            
            // Parse date
            let day, month, year;
            if (payment.date.includes('.')) {
              [day, month, year] = payment.date.split('.');
            } else if (payment.date.includes('/')) {
              [day, month, year] = payment.date.split('/');
            } else {
              return;
            }
            
            const paymentMonth = parseInt(month);
            const paymentYear = parseInt(year);
            
            // Check if this payment is due in current month
            if (paymentMonth === currentMonth && paymentYear === currentYear) {
              const monthKey = (index + 1).toString();
              const status = loan.scheduled_payments_status?.[monthKey] || 'pending';
              
              // Only include unpaid (pending, invoice_sent, overdue)
              if (status === 'pending' || status === 'invoice_sent' || status === 'overdue') {
                const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const daysOverdue = status === 'overdue' ? Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)) : 0;
                
                // Calculate penalty interest if overdue
                let penaltyInterest = 0;
                if (status === 'overdue' && daysOverdue > 0) {
                  penaltyInterest = calculatePenaltyInterest(
                    payment.payment || 0,
                    payment.date,
                    settingsResponse.data.drattarvextir || 15.25
                  );
                }
                
                unpaidList.push({
                  loanId: loan.id,
                  loanNumber: loan.loan_number,
                  borrowerName: loan.borrower_info?.full_name || 'N/A',
                  borrowerEmail: loan.borrower_info?.email || 'N/A',
                  borrowerSSN: loan.borrower_info?.ssn || 'N/A',
                  loanType: loan.loan_type,
                  paymentDate: payment.date,
                  dueDate: dueDate,
                  paymentAmount: payment.payment || 0,
                  principal: payment.principal || 0,
                  interest: payment.interest || 0,
                  greidslus: loan.greidslus || settingsResponse.data.greidslus || 250,
                  status: status,
                  monthNumber: index + 1,
                  daysOverdue: daysOverdue,
                  penaltyInterest: penaltyInterest,
                  totalDue: (payment.payment || 0) + penaltyInterest
                });
              }
            }
          });
        }
      });
      
      // Sort by status (overdue first), then by due date
      unpaidList.sort((a, b) => {
        const statusOrder = { 'overdue': 0, 'invoice_sent': 1, 'pending': 2 };
        if (statusOrder[a.status] !== statusOrder[b.status]) {
          return statusOrder[a.status] - statusOrder[b.status];
        }
        return a.dueDate - b.dueDate;
      });
      
      setUnpaidInvoices(unpaidList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'Í bið', color: 'bg-yellow-100 text-yellow-800' },
      'invoice_sent': { label: 'Reikningur sendur', color: 'bg-blue-100 text-blue-800' },
      'overdue': { label: 'Í vanskilum', color: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const totalUnpaid = unpaidInvoices.reduce((sum, inv) => sum + inv.totalDue, 0);
  const totalPenalty = unpaidInvoices.reduce((sum, inv) => sum + inv.penaltyInterest, 0);

  if (loading) {
    return <div className="p-8">Hleð ógreiddum reikningum...</div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          {!embedded && (
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Til baka
            </Button>
          )}
          <h1 className="text-3xl font-bold">Ógreiddir Reikningar</h1>
          <p className="text-gray-600 mt-2">Yfirlit yfir alla ógreidda reikninga í þessum mánuði</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-600">Fjöldi ógreiddra</div>
              <div className="text-2xl font-bold text-red-600">{unpaidInvoices.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-600">Heildarupphæð</div>
              <div className="text-2xl font-bold">{formatIcelandicNumber(Math.round(totalUnpaid))} kr.</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-600">Dráttarvextir</div>
              <div className="text-2xl font-bold text-orange-600">{formatIcelandicNumber(Math.round(totalPenalty))} kr.</div>
            </CardContent>
          </Card>
        </div>

        {/* Unpaid Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle>Ógreiddir reikningar</CardTitle>
          </CardHeader>
          <CardContent>
            {unpaidInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                <p>Engir ógreiddir reikningar í þessum mánuði</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lántaki</TableHead>
                    <TableHead>Kennitala</TableHead>
                    <TableHead>Gjalddagi</TableHead>
                    <TableHead>Upphæð</TableHead>
                    <TableHead>Dráttarvextir</TableHead>
                    <TableHead>Samtals</TableHead>
                    <TableHead>Dagar í vanskilum</TableHead>
                    <TableHead>Staða</TableHead>
                    <TableHead>Aðgerðir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unpaidInvoices.map((invoice, index) => (
                    <TableRow key={index} className={invoice.status === 'overdue' ? 'bg-red-50' : ''}>
                      <TableCell>
                        <div className="font-medium">{invoice.borrowerName}</div>
                        <div className="text-xs text-gray-500">{invoice.borrowerEmail}</div>
                      </TableCell>
                      <TableCell className="text-sm">{invoice.borrowerSSN}</TableCell>
                      <TableCell className="text-sm">{invoice.paymentDate}</TableCell>
                      <TableCell className="font-medium">
                        {formatIcelandicNumber(Math.round(invoice.paymentAmount))} kr.
                      </TableCell>
                      <TableCell className="text-orange-600">
                        {invoice.penaltyInterest > 0 ? `${formatIcelandicNumber(Math.round(invoice.penaltyInterest))} kr.` : '-'}
                      </TableCell>
                      <TableCell className="font-bold">
                        {formatIcelandicNumber(Math.round(invoice.totalDue))} kr.
                      </TableCell>
                      <TableCell>
                        {invoice.daysOverdue > 0 ? (
                          <span className="text-red-600 font-semibold">{invoice.daysOverdue} dagar</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/greidslur/${invoice.loanId}`)}
                          title="Skoða greiðsluáætlun"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


